<template>
  <DialogWrapper
    class="add-question-dialog"
    width="720px"
    top="5vh"
    :show.sync="innerShow"
    :title="$t(`${i18nPrefix}.btn.addQuestion`)"
    :loading="loading"
    :disabledSaveBtn="selectedIds.length === 0"
    :closeOnClickModal="false"
    @save="handleConfirm"
  >
    <div class="question-type-filter">
      <div class="filter-title">{{ $t(`${i18nPrefix}.label.questionType`) }}</div>
      <el-button
        v-for="item in typeFilters"
        :key="item.value"
        :class="activeType === item.value && 'active'"
        @click="activeType = item.value"
      >
        {{ item.label }}
      </el-button>
    </div>
    <el-input
      v-model="keyword"
      class="question-search"
      prefix-icon="el-icon-search"
      :placeholder="$t(`${i18nPrefix}.placeholder.searchQuestion`)"
    />
    <div class="selector-toolbar">
      <span class="selected-count">{{ $t(`${i18nPrefix}.message.selectedQuestion`, { count: selectedIds.length }) }}</span>
      <span class="refresh-btn" @click="fetchQuestions"><i class="iconfont iconrefresh2"></i>{{ $t('common.btn.refresh') }}</span>
      <el-button class="add-new-btn" @click="handleAddQuestion">{{ $t(`${i18nPrefix}.btn.addNewQuestion`) }}</el-button>
    </div>
    <div v-rect-loading="loading" ref="questionListRef" class="question-card-list" :style="customScheduleContentStyle">
      <div
        v-for="question in filteredQuestionList"
        :key="question.id"
        class="question-card"
        :class="{ selected: isSelectedQuestion(question.id) }"
        @click="toggleQuestion(question.id)"
      >
        <el-checkbox
          class="question-checkbox"
          :value="isSelectedQuestion(question.id)"
          @change="toggleQuestion(question.id)"
          @click.native.stop
        />
        <div class="question-content">
          <span class="type-tag" :class="`type-${question.type}`">{{ getTypeLabel(question.type) }}</span>
          <div class="question-title">{{ question.titleI18n }}</div>
          <div v-if="question.optionsI18n" class="question-options">{{ question.optionsI18n }}</div>
        </div>
      </div>
      <div v-if="!filteredQuestionList.length && !loading" class="empty-text">{{ $t('common.label.noData') }}</div>
    </div>
  </DialogWrapper>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import { HM_QUESTION_STATUS, HM_QUESTION_TYPE as QUESTION_TYPE } from '@/constant/questionnaire'
import { getI18nPropertyValue } from '@/i18n'

export default {
  components: {
    DialogWrapper
  },
  props: {
    show: {
      type: Boolean,
      required: true
    },
    selectedQuestionIds: {
      type: Array,
      default () {
        return []
      }
    },
    keepStateOnOpen: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      loading: false,
      i18nPrefix: 'admin.company.questionnaireManagement',
      keyword: '',
      activeType: 'all',
      questions: [],
      selectedIds: [],
      hasScrollBar: false
    }
  },
  computed: {
    innerShow: {
      get () {
        return this.show
      },
      set (val) {
        this.$emit('update:show', val)
      }
    },
    typeFilters () {
      return [
        { value: 'all', label: this.$t(`${this.i18nPrefix}.btn.all`) },
        { value: QUESTION_TYPE.RATING, label: this.$t(`${this.i18nPrefix}.btn.rating`) },
        { value: QUESTION_TYPE.TEXT, label: this.$t(`${this.i18nPrefix}.btn.text`) },
        { value: QUESTION_TYPE.SINGLE, label: this.$t(`${this.i18nPrefix}.btn.single`) },
        { value: QUESTION_TYPE.MULTIPLE, label: this.$t(`${this.i18nPrefix}.btn.multiple`) },
        { value: QUESTION_TYPE.DATE, label: this.$t(`${this.i18nPrefix}.btn.date`) }
      ]
    },
    filteredQuestionList () {
      const keyword = this.keyword.trim().toLowerCase()
      let list = _.chain(this.questions)
        .filter(item => !_.includes(this.selectedQuestionIds, item.id))
        .value()
      if (this.activeType !== 'all') {
        list = _.filter(list, item => item.type === this.activeType)
      }
      if (keyword) {
        list = _.filter(list, item => {
          return _.chain([item.titleCn, item.titleEn, item.remark])
            .some(text => _.includes((text || '').toLowerCase(), keyword))
            .value()
        })
      }
      return list
    },
    customScheduleContentStyle () {
      return {
        marginRight: `${this.hasScrollBar ? -26 : -8}px`
      }
    }
  },
  methods: {
    ...mapActions('questionnaire', ['searchHmQuestions']),
    getTypeLabel (type) {
      const option = _.find(this.typeFilters, item => item.value === type)
      return option ? option.label : type
    },
    isSelectedQuestion (id) {
      return _.includes(this.selectedIds, id)
    },
    joinOptions (options, key) {
      return _.chain(options || []).map(key).filter().join('\n').value()
    },
    normalizeOptions (options) {
      return _.sortBy(options || [], ['sort_order', 'id'])
    },
    joinOptionLabels (options) {
      return _.chain(options || [])
        .map(item => getI18nPropertyValue(item, 'label_zh', 'label_en'))
        .filter()
        .join('\n')
        .value()
    },
    normalizeQuestion (question) {
      const source = question || {}
      const options = this.normalizeOptions(source.options)
      const isLocked = Boolean(source.is_locked)
      const isInherited = Boolean(source.is_inherited)
      return {
        id: source.id,
        type: source.question_type,
        titleI18n: getI18nPropertyValue(source, 'title_zh', 'title_en'),
        titleCn: source.title_zh,
        titleEn: source.title_en,
        remark: isInherited ? 'Inherited' : '',
        status: source.status,
        canEdit: !isLocked && !isInherited,
        isLocked,
        isInherited,
        options,
        optionsI18n: this.joinOptionLabels(options),
        optionsCn: this.joinOptions(options, 'label_zh'),
        optionsEn: this.joinOptions(options, 'label_en'),
        raw: {
          ...source,
          options
        }
      }
    },
    async fetchQuestions () {
      this.loading = true
      try {
        const resp = await this.searchHmQuestions({
          include_inherited: true,
          page_size: 100,
          status: HM_QUESTION_STATUS.ENABLED
        })
        this.questions = _.chain(resp)
          .thru(data => {
            if (_.isArray(data)) {
              return data
            }
            return _.get(data, 'data', [])
          })
          .map(item => this.normalizeQuestion(item))
          .value()
      } finally {
        this.loading = false
      }
    },
    resetAndFetch () {
      this.keyword = ''
      this.activeType = 'all'
      this.selectedIds = []
      this.fetchQuestions()
    },
    toggleQuestion (id) {
      if (_.includes(this.selectedIds, id)) {
        this.selectedIds = _.filter(this.selectedIds, item => item !== id)
        return
      }
      this.selectedIds = [...this.selectedIds, id]
    },
    handleConfirm () {
      const selectedQuestions = _.chain(this.questions)
        .filter(item => _.includes(this.selectedIds, item.id))
        .value()
      this.$emit('confirm', selectedQuestions)
      this.innerShow = false
    },
    handleAddQuestion () {
      this.$emit('create-question')
    }
  },
  watch: {
    show: {
      immediate: true,
      handler (val) {
        if (val) {
          if (!this.keepStateOnOpen) {
            this.resetAndFetch()
            return
          }
          if (!this.keepStateOnOpen || !this.questions.length) {
            this.fetchQuestions()
          }
        }
      }
    },
    filteredQuestionList: {
      immediate: true,
      handler () {
        this.$nextTick(() => {
          this.hasScrollBar = this.$refs.questionListRef?.scrollHeight > this.$refs.questionListRef?.clientHeight
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.add-question-dialog {
  .question-type-filter {
    margin-bottom: 16px;

    .filter-title {
      margin-bottom: 12px;
      color: #606266;
      font-weight: 500;
      line-height: 32px;
    }

    .el-button {
      padding: 8px 16px;
      border-radius: 2px;
      font-size: 12px;
      white-space: normal;

      &.active {
        color: #ffffff;
        background: #0096cc;
        border-color: #0096cc;
      }
    }
  }

  .question-search {
    margin-bottom: 16px;
  }

  .selector-toolbar {
    display: flex;
    align-items: center;
    margin-bottom: 14px;

    .selected-count {
      color: #0096cc;
      white-space: normal;
    }

    .refresh-btn {
      margin-left: auto;
      margin-right: 12px;
      color: #606266;
      cursor: pointer;
      white-space: normal;

      i {
        margin-right: 4px;
      }
    }

    .add-new-btn {
      border-color: #0096cc;
      color: #0096cc;
      padding: 8px 12px;
      white-space: normal;
    }
  }

  .question-card-list {
    max-height: 40vh;
    max-height: 40dvh;
    overflow-y: auto;
    padding-right: 8px;

    .question-card {
      display: flex;
      min-height: 76px;
      margin-bottom: 16px;
      padding: 14px 12px;
      border: 1px solid #dcdfe6;
      cursor: pointer;

      &.selected {
        background: rgba(0, 150, 204, 0.08);
        border-color: #0096cc;
      }

      .question-checkbox {
        flex: 0 0 auto;
        margin-right: 14px;
        padding-top: 2px;
      }

      .question-content {
        min-width: 0;

        .type-tag {
          display: inline-block;
          padding: 4px 8px;
          border-radius: 2px;
          font-size: 10px;
          line-height: 10px;
          white-space: normal;

          &.type-rating {
            color: #0096cc;
            background: rgba(0, 150, 204, 0.1);
          }

          &.type-text {
            color: #51bf82;
            background: rgba(81, 191, 130, 0.1);
          }

          &.type-single_select {
            color: #f4a958;
            background: rgba(244, 169, 88, 0.1);
          }

          &.type-multi_select {
            color: #b091ff;
            background: rgba(176, 145, 255, 0.1);
          }

          &.type-date {
            color: #d95f63;
            background: rgba(217, 95, 99, 0.1);
          }
        }

        .question-title {
          margin-top: 6px;
          color: #303133;
          line-height: 18px;
          white-space: normal;
          word-break: normal;
        }

        .question-options {
          padding-left: 20px;
          margin-top: 4px;
          color: #606266;
          font-size: 12px;
          line-height: 18px;
          white-space: pre-line;
          word-break: normal;
        }
      }
    }

    .empty-text {
      padding: 48px 0;
      color: #909399;
      text-align: center;
    }
  }
}
</style>
